/**
 * The AI extract contract for the anatomy (#998 slice 4) — the SAME provider
 * guard InteractionsPage's SmartPaste uses, so on a box with no copilot (mbn)
 * this degrades to a NAMED "Copilot not configured" refusal and the card falls
 * back to its deterministic fields. It never becomes a dead button.
 *
 * SmartPaste's contract was `{kind,title,note}` — the interaction HEADER, never
 * its anatomy. This extends it to a LIST of structured items — decisions and
 * assigned actions — which the card surfaces for confirm-then-write. The write
 * still goes through the governed verbs (capture.ts); the AI only prefills.
 */
import { useMutation } from '@tanstack/react-query'
import { accessToken } from '@solum/auth'

export interface ExtractedItem {
  kind: 'decision' | 'action' | 'open_point'
  title: string
  due?: string | null
  assignee?: string | null
}

const KINDS = new Set(['decision', 'action', 'open_point'])

export function useSmartExtractStructured() {
  const env = import.meta.env as Record<string, string | undefined>
  const url = (env.VITE_COPILOT_URL ?? '').replace(/\/copilot$/, '/extract')
  return useMutation({
    mutationFn: async ({ text }: { text: string }): Promise<ExtractedItem[]> => {
      const tok = await accessToken()
      // The guard, word-for-word the one SmartPaste raises — a box with no
      // provider says so, and the caller degrades to the deterministic fields.
      if (!url || !tok) throw new Error('Copilot not configured (VITE_COPILOT_URL).')
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${tok}` },
        body: JSON.stringify({
          text,
          // the structured contract: ask for the anatomy, not the header
          items: { kinds: ['decision', 'action', 'open_point'], fields: ['kind', 'title', 'due', 'assignee'] },
        }),
      })
      if (!r.ok) throw new Error(`extract ${r.status}`)
      const j = (await r.json()) as { items?: ExtractedItem[] }
      return (j.items ?? [])
        .filter((it) => it && KINDS.has(it.kind) && typeof it.title === 'string' && it.title.trim().length > 0)
        .map((it) => ({ kind: it.kind, title: String(it.title).trim(), due: it.due ?? null, assignee: it.assignee ?? null }))
    },
  })
}
